import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowUp, ChevronLeft, Sparkles, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, useSyncExternalStore, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { apiFetch, ApiProblem, newIdempotencyKey } from '../lib/api.ts';
import { useScreen } from '../lib/layout.ts';
import { useLayer } from '../lib/modal-focus.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Textarea } from './ui/textarea.tsx';

/**
 * „KI-Copilot“ (Story 19.11, FR-101, EXPERIENCE §4.1, §4.13, DESIGN „KI-Copilot-Leiste“ und Knopf „KI-Copilot“; Name seit
 * Ticket 1.243; stabile Kennungen wie `ask_till` bleiben): Knopf im
 * Kopf, Leiste rechts über dem Inhalt, am Handy Vollbild mit „Zurück“ und Eingabe unten. Sie kennt den
 * geöffneten Datensatz (Zeile „Bezug: Auftrag 20111“), zeigt den eigenen Verlauf, Antworten mit Quelle und Karten
 * (Vorschlag, Entwurf, Vorhaben) mit „Ansehen“. Ausgeführt wird nichts ohne Klick.
 *
 * Aufträge an die KI (Story 19.12, FR-126, EXPERIENCE §4.13 „Arbeiten auf Anweisung“): Karte „Auftrag“ mit Vorschau,
 * Knopf „Ausführen“, danach „Erledigt“ mit „Rückgängig“ – nur Wiedervorlagen und interne Auftrags-Tags.
 */

export const ASK_TILL_TEXTS = {
  button: 'KI-Copilot',
  title: 'KI-Copilot',
  placeholder: 'Frage stellen, z. B. „Warum ist der noch nicht versendet?“',
  send: 'Fragen',
  close: 'KI-Copilot schließen',
  back: 'Zurück',
  waiting: 'Der KI-Copilot denkt nach …',
  empty: 'Frag zum geöffneten Datensatz oder allgemein. Die KI legt höchstens Vorschläge, Entwürfe oder Vorhaben an – ausgeführt wird erst nach deinem Klick.',
  view: 'Ansehen',
  context: (label: string) => `Bezug: ${label}`,
  source: 'Quelle:',
  limit: (used: number, max: number) => `Heute ${used} von ${max} Fragen`,
  cardKind: {
    suggestion: 'Vorschlag',
    draft: 'Entwurf',
    plan: 'Vorhaben',
    instruction: 'Auftrag',
  } as const,
  confirm: 'Ausführen',
  undo: 'Rückgängig',
  actionFailed: 'Das hat nicht geklappt. Bitte erneut versuchen.',
  packageNo: (n: number) => `Änderungspaket Nr. ${n}`,
} as const;

type CardKind = keyof typeof ASK_TILL_TEXTS.cardKind;

interface AskItem {
  id: string;
  question: string;
  context: {
    type: string;
    id: string;
    label: string;
    path: string | null;
  } | null;
  status: 'waiting' | 'answered' | 'refused' | 'failed';
  text: string | null;
  sources: { label: string; path: string | null }[];
  cards: AskCardItem[];
}

type InstructionState = 'awaiting' | 'running' | 'done' | 'conflict' | 'undoing' | 'undone' | 'discarded';

interface AskCardItem {
  kind: CardKind;
  id: string;
  title: string;
  path: string;
  lines?: string[];
  instruction: {
    number: number;
    state: InstructionState;
    stateText: string;
    lines: string[];
    messages: string[];
    canConfirm: boolean;
    canUndo: boolean;
  } | null;
}

/** Auftrag noch in Arbeit beim Worker (Leiste fragt kurz nach). */
const busyInstruction = (item: AskItem) => item.cards.some((c) => c.instruction?.state === 'running' || c.instruction?.state === 'undoing');

export interface AskHistory {
  state: 'live' | 'probe' | 'paused';
  pausedText: string | null;
  usedToday: number;
  dailyLimit: number;
  items: AskItem[];
}

const QUERY_KEY = ['assistant', 'ask'] as const;

/** Eigener Verlauf und Tageszähler des KI-Copiloten (dieselbe Abfrage wie im Panel; z. B. Karte „KI“ im Profil). */
export function useAskHistory() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<AskHistory>(getToken, '/api/v1/assistant/ask'),
  });
}

// ---------------------------------------------------------------------------
// Offen/zu (eine Leiste je App)

let open = false;
const listeners = new Set<() => void>();
let opener: HTMLElement | null = null;
/** Vorbelegte Frage (Frage-Feld im Datensatz-Kopf, Redesign 31.10.2026): steht nur im Eingabefeld, gesendet wird erst per Klick. */
let draft = '';
/** Zählt jede neue Vorbelegung, damit eine schon offene Leiste die Frage übernimmt (Zweit-Review #1639). */
let draftSeq = 0;
/** Frage sofort stellen (nur, wenn die Person selbst „Senden“ gedrückt hat, z. B. im Frage-Feld der Startseite). */
let pendingSend = false;

function setOpen(next: boolean, from: HTMLElement | null = null): void {
  if (next && !open) opener = from;
  open = next;
  for (const l of listeners) l();
  if (!next && opener) {
    const el = opener;
    opener = null;
    el.focus();
  }
}

export function openAskTill(from: HTMLElement | null = null, question = '', opts: { send?: boolean } = {}): void {
  draft = question;
  pendingSend = Boolean(opts.send && question.trim());
  if (question) draftSeq += 1;
  if (open) {
    // Leiste schon offen: nur die Frage ins Feld (nichts wird gesendet).
    for (const l of listeners) l();
    return;
  }
  setOpen(true, from);
}

function useDraftSeq(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => draftSeq,
    () => draftSeq,
  );
}

function takeDraft(): string {
  const d = draft;
  draft = '';
  return d;
}

function useAskTillOpen(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => open,
    () => open,
  );
}

// ---------------------------------------------------------------------------
// Bezug aus der Adresse (geöffneter Datensatz)

const CONTEXT_ROUTES: readonly { pattern: RegExp; type: string }[] = [
  { pattern: /^\/auftraege\/([0-9a-f-]{36})$/i, type: 'order' },
  { pattern: /^\/kunden\/([0-9a-f-]{36})$/i, type: 'customer' },
  { pattern: /^\/artikel\/([0-9a-f-]{36})$/i, type: 'article' },
];

/** Bezug zur aktuellen Seite: Auftrag, Kunde oder Artikel; sonst `null`. */
export function askContextOf(pathname: string): { type: string; id: string } | null {
  for (const r of CONTEXT_ROUTES) {
    const m = r.pattern.exec(pathname);
    if (m) return { type: r.type, id: m[1]!.toLowerCase() };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Knopf im Kopf

/**
 * Knopf „KI-Copilot“ (DESIGN „Kopfzeile“, Ticket 1.243): weiße Pille in Kontrollhöhe mit Rand 1,5 px Dunkelblau, Funke in
 * Orange, ohne Unterzeile; am Handy runder Symbolknopf in der Kopfzeile. Öffnet die Leiste „KI-Copilot“.
 */
export function AskTillButton({ round }: { round?: boolean }) {
  const ref = useRef<HTMLButtonElement>(null);
  const isOpen = useAskTillOpen();
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => setOpen(!isOpen, ref.current)}
      aria-expanded={isOpen}
      aria-label={ASK_TILL_TEXTS.button}
      title={ASK_TILL_TEXTS.button}
      className={cn(
        'inline-flex flex-none cursor-pointer items-center justify-center gap-space-2 rounded-full border border-line bg-transparent font-medium text-ink transition-colors duration-150 hover:bg-hover',
        round ? 'size-10 pointer-coarse:size-11' : 'h-10 px-space-3 pointer-coarse:h-11 lg:px-space-4',
      )}
    >
      <Sparkles size={18} strokeWidth={1.75} aria-hidden className="flex-none text-brand-orange" />
      {round ? null : (
        <span aria-hidden className="text-md whitespace-nowrap max-lg:hidden">
          {ASK_TILL_TEXTS.button}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Frage-Feld im Datensatz-Kopf (Redesign 31.10.2026, Vorbild Variante C)

const ASK_SUGGESTIONS: Record<string, readonly string[]> = {
  customer: ['Was hat der Kunde zuletzt gekauft?', 'Offene Posten erklären', 'Wie entwickelt sich der Umsatz?'],
  order: ['Warum ist der noch nicht versendet?', 'Was ist an diesem Auftrag besonders?', 'Gab es Änderungen nach dem Eingang?'],
  article: ['Wie hat sich der Absatz entwickelt?', 'Wann sollte ich nachbestellen?', 'Welche Kunden kaufen diesen Artikel?'],
};

/**
 * Ruhiges Frage-Feld unter dem Datensatz-Kopf (Auftrag, Kunde, Artikel): sieht aus wie ein Eingabefeld, öffnet die Leiste
 * „KI-Copilot“ mit Bezug auf den Datensatz. Vorschläge darunter füllen nur das Eingabefeld der Leiste – gefragt wird erst
 * per Klick auf „Fragen“; die KI legt höchstens Vorschläge oder Entwürfe an (AD-16).
 */
export function AskRecordBar({ name }: { name: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const context = askContextOf(pathname);
  const ref = useRef<HTMLButtonElement>(null);
  if (!context) return null;
  const suggestions = ASK_SUGGESTIONS[context.type] ?? [];
  return (
    <div data-testid="frage-feld" className="flex min-w-0 flex-col gap-space-3">
      <button
        ref={ref}
        type="button"
        onClick={() => openAskTill(ref.current)}
        className="group flex h-14 w-full min-w-0 cursor-text items-center gap-space-3 rounded-full border border-line bg-card pr-space-2 pl-space-5 text-left shadow-ask transition-[box-shadow,border-color] duration-200 ease-[var(--motion-ease)] hover:border-line-strong"
      >
        <Sparkles size={18} strokeWidth={1.6} aria-hidden className="flex-none text-ink-3 transition-colors duration-200 group-hover:text-brand" />
        <span className="min-w-0 flex-1 truncate text-row text-ink-3">Frag etwas zu {name} …</span>
        <span aria-hidden className="grid size-10 flex-none place-items-center rounded-full bg-fill text-ink-3 transition-colors duration-200 group-hover:bg-primary group-hover:text-on-brand">
          <ArrowUp size={18} strokeWidth={1.75} />
        </span>
      </button>
      {suggestions.length > 0 ? (
        <ul aria-label="Frage-Vorschläge" className="-mx-space-4 flex list-none gap-space-2 overflow-x-auto px-space-4 py-0 [scrollbar-width:none] tablet:mx-0 tablet:flex-wrap tablet:overflow-visible tablet:px-0">
          {suggestions.map((q) => (
            <li key={q}>
              <button
                type="button"
                onClick={(e) => openAskTill(e.currentTarget, q)}
                className="inline-flex h-9 cursor-pointer items-center whitespace-nowrap rounded-full border border-line px-space-4 text-caption text-ink-2 transition-colors duration-150 hover:bg-hover hover:text-ink pointer-coarse:h-11"
              >
                {q}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leiste

/** Die Leiste (einmal in der App-Hülle). */
export function AskTillHost() {
  const isOpen = useAskTillOpen();
  return isOpen ? <AskTillPanel /> : null;
}

function AskTillPanel() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const phone = useScreen() === 'phone';
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const context = askContextOf(pathname);
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const [question, setQuestion] = useState(takeDraft);
  // Vorschlag bei schon offener Leiste: Frage ins Feld (Zustand beim Rendern nachziehen) – gefragt wird erst mit „Fragen“.
  const seq = useDraftSeq();
  const [seenSeq, setSeenSeq] = useState(seq);
  if (seq !== seenSeq) {
    setSeenSeq(seq);
    if (draft) setQuestion(takeDraft());
  }
  useEffect(() => {
    input.current?.focus();
  }, [seq]);
  const close = () => setOpen(false);
  useLayer(true, { root: panel, trap: phone, onEscape: close });

  const history = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<AskHistory>(getToken, '/api/v1/assistant/ask'),
    // Solange eine Frage auf die KI wartet, kurz nachfragen.
    // Ebenso, solange ein Auftrag ausgeführt oder zurückgenommen wird.
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.status === 'waiting' || busyInstruction(i)) ? 1_500 : false),
  });
  const ask = useMutation({
    mutationFn: (q: string) =>
      apiFetch<{ askId: string }>(getToken, '/api/v1/assistant/ask', {
        method: 'POST',
        body: { question: q, context },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      setQuestion('');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  // Mit `send` (Person hat im Frage-Feld selbst gesendet): die vorbelegte Frage gleich stellen – die KI antwortet nur,
  // Vorschläge/Entwürfe brauchen weiter einen Klick.
  useEffect(() => {
    if (!pendingSend || !question.trim()) return;
    pendingSend = false;
    ask.mutate(question);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Öffnen bzw. neuer Vorbelegung
  }, [seq]);

  useEffect(() => {
    input.current?.focus();
  }, []);
  const items = history.data?.items ?? [];
  const lastId = items.at(-1)?.id;
  const lastStatus = items.at(-1)?.status;
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId, lastStatus]);

  const data = history.data;
  const paused = data?.state === 'paused';
  const waiting = items.some((i) => i.status === 'waiting');
  const error = ask.error instanceof ApiProblem ? ask.error.detail : ask.error ? 'Die Frage konnte nicht gesendet werden.' : null;
  const canSend = question.trim().length > 0 && !ask.isPending && !waiting && !paused;
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (canSend) ask.mutate(question);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sendet, Umschalt+Enter macht eine neue Zeile.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      ref={panel}
      role="dialog"
      aria-modal={phone}
      aria-labelledby={headingId}
      data-testid="ki-copilot"
      data-presentation={phone ? 'phone' : 'panel'}
      className={cn(
        'fixed z-40 flex flex-col overflow-x-hidden bg-[var(--glass)] backdrop-blur-panel',
        phone ? 'inset-0 bg-card' : 'inset-y-0 right-0 w-panel-w max-w-full rounded-l-card shadow-panel',
      )}
      style={phone ? undefined : { top: 'var(--banner-h, 0px)' }}
    >
      <div className={cn('flex flex-none items-center gap-2 border-b border-line py-3', phone ? 'px-4' : 'px-5')}>
        {phone ? (
          <Button variant="secondary" onClick={close} className="flex-none pl-3">
            <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
            {ASK_TILL_TEXTS.back}
          </Button>
        ) : null}
        <h2 id={headingId} className="type-card-title flex min-w-0 flex-1 items-center gap-2">
          <Sparkles size={18} strokeWidth={1.75} aria-hidden className="flex-none text-brand-orange" />
          {ASK_TILL_TEXTS.title}
        </h2>
        {phone ? null : (
          <button
            type="button"
            onClick={close}
            aria-label={ASK_TILL_TEXTS.close}
            className="grid size-10 cursor-pointer place-items-center rounded-full text-ink-2 pointer-coarse:size-11"
          >
            <X size={18} strokeWidth={1.5} aria-hidden />
          </button>
        )}
      </div>
      <div ref={list} className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain py-4', phone ? 'px-4' : 'px-5')}>
        {items.length === 0 ? <p className="type-meta text-ink-3">{ASK_TILL_TEXTS.empty}</p> : null}
        <ol aria-label="Verlauf" className="flex list-none flex-col gap-5 p-0">
          {items.map((item) => (
            <AskEntry key={item.id} item={item} />
          ))}
        </ol>
      </div>
      <form
        onSubmit={submit}
        className={cn('flex flex-none flex-col gap-2 border-t border-line py-3', phone ? 'px-4 pb-[max(12px,env(safe-area-inset-bottom))]' : 'px-5')}
      >
        {context ? <ContextLine context={context} /> : null}
        {paused && data?.pausedText ? (
          <p role="status" data-testid="ki-copilot-pausiert" className="type-meta text-ink-2">
            {data.pausedText}
          </p>
        ) : data?.state === 'probe' && data.pausedText ? (
          <p role="status" className="type-meta text-ink-3">
            {data.pausedText}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="type-meta text-clarify">
            {error}
          </p>
        ) : null}
        <label className="sr-only" htmlFor={`${headingId}-q`}>
          Frage an den KI-Copilot
        </label>
        <Textarea
          id={`${headingId}-q`}
          ref={input}
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={ASK_TILL_TEXTS.placeholder}
          disabled={paused}
          className="min-h-11 resize-none px-3 py-2 text-field-phone outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-row"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="type-meta text-ink-3 tabular-nums">{data ? ASK_TILL_TEXTS.limit(data.usedToday, data.dailyLimit) : ''}</span>
          <Button type="submit" disabled={!canSend}>
            {ASK_TILL_TEXTS.send}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Zeile „Bezug: Auftrag 20111“ – Bezeichnung vom Server (mit den Rechten der Person); bis dahin die Art. */
function ContextLine({ context }: { context: { type: string; id: string } }) {
  const { getToken } = useSession();
  const label = useQuery({
    queryKey: ['assistant', 'ask-context', context.type, context.id],
    queryFn: () =>
      apiFetch<{ label: string | null }>(
        getToken,
        `/api/v1/assistant/ask/context?type=${encodeURIComponent(context.type)}&id=${encodeURIComponent(context.id)}`,
      ),
    retry: false,
  });
  if (label.isError || label.data?.label === null) return null;
  const text =
    label.data?.label ??
    {
      order: 'geöffneter Auftrag',
      customer: 'geöffneter Kunde',
      article: 'geöffneter Artikel',
    }[context.type] ??
    'geöffneter Datensatz';
  return (
    <p data-testid="ki-copilot-bezug" className="type-meta text-ink-2">
      {ASK_TILL_TEXTS.context(text)}
    </p>
  );
}

function AskEntry({ item }: { item: AskItem }) {
  return (
    <li data-testid="ki-copilot-eintrag" data-status={item.status} className="flex flex-col gap-2">
      <p className="type-meta text-ink-3">
        {item.context ? <span className="block">{ASK_TILL_TEXTS.context(item.context.label)}</span> : null}
        <span className="text-row text-ink-2 [overflow-wrap:anywhere]">{item.question}</span>
      </p>
      {item.status === 'waiting' ? (
        <p role="status" className="text-row text-ink-3">
          {ASK_TILL_TEXTS.waiting}
        </p>
      ) : (
        <p data-testid="ki-copilot-antwort" className="text-row text-ink [overflow-wrap:anywhere]">
          {item.text}
        </p>
      )}
      {item.sources.length > 0 ? (
        <p className="type-meta text-ink-3">
          {ASK_TILL_TEXTS.source}{' '}
          {item.sources.map((s, i) => (
            <span key={`${s.label}-${i}`}>
              {i > 0 ? ' · ' : null}
              {s.path ? (
                <Link to={s.path} className="inline-flex min-h-6 items-center text-ink-2 underline-offset-2 hover:text-brand hover:underline touch-hit">
                  {s.label}
                </Link>
              ) : (
                s.label
              )}
            </span>
          ))}
        </p>
      ) : null}
      {item.cards.map((card) =>
        card.kind === 'instruction' ? (
          <InstructionCard key={card.id} askId={item.id} card={card} />
        ) : (
          <div
            key={card.id}
            data-testid="ki-copilot-karte"
            data-kind={card.kind}
            className="card-box flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <span className="type-meta block text-ink-3">{ASK_TILL_TEXTS.cardKind[card.kind]}</span>
              <span className="block text-row text-ink [overflow-wrap:anywhere]">{card.title}</span>
            </div>
            <ViewLink path={card.path} />
          </div>
        ),
      )}
    </li>
  );
}

function ViewLink({ path }: { path: string }) {
  return (
    <Link
      to={path}
      onClick={() => setOpen(false)}
      className="inline-flex h-11 flex-none items-center rounded-full border border-chip-line bg-chip px-4 text-row text-ink"
    >
      {ASK_TILL_TEXTS.view}
    </Link>
  );
}

/**
 * Karte „Auftrag“ (Story 19.12): Vorschau in Sätzen und Zustand; „Ausführen“ solange sie wartet, „Rückgängig“ wenn erledigt.
 * Ausgeführt wird im Hintergrund (Worker); die Leiste fragt nach, bis „Erledigt“ dasteht.
 */
function InstructionCard({ askId, card }: { askId: string; card: AskCardItem }) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const instruction = card.instruction;
  const action = useMutation({
    mutationFn: (kind: 'confirm' | 'undo') =>
      apiFetch<AskItem>(getToken, `/api/v1/assistant/ask/${encodeURIComponent(askId)}/instructions/${encodeURIComponent(card.id)}/${kind}`, {
        method: 'POST',
        idempotencyKey: newIdempotencyKey(),
      }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const lines = instruction?.lines.length ? instruction.lines : (card.lines ?? []);
  const error = action.error instanceof ApiProblem ? action.error.detail : action.error ? ASK_TILL_TEXTS.actionFailed : null;
  return (
    <div
      data-testid="ki-copilot-karte"
      data-kind="instruction"
      data-state={instruction?.state ?? ''}
      className="card-box flex flex-col gap-2"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <span className="type-meta block text-ink-3">
            {ASK_TILL_TEXTS.cardKind.instruction}
            {instruction ? ` · ${ASK_TILL_TEXTS.packageNo(instruction.number)}` : null}
          </span>
          <span className="block text-row text-ink [overflow-wrap:anywhere]">{card.title}</span>
        </div>
        <ViewLink path={card.path} />
      </div>
      {lines.length > 0 ? (
        <ul data-testid="ki-copilot-vorschau" className="flex list-disc flex-col gap-1 pl-5 text-row text-ink-2">
          {lines.map((line, i) => (
            <li key={`${i}-${line}`} className="[overflow-wrap:anywhere]">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
      {instruction ? (
        <p role="status" data-testid="ki-copilot-auftrag-stand" className="type-meta text-ink-2">
          {instruction.stateText}
        </p>
      ) : null}
      {instruction?.messages.map((m, i) => (
        <p key={`${i}-${m}`} className="type-meta text-ink-3 [overflow-wrap:anywhere]">
          {m}
        </p>
      ))}
      {error ? (
        <p role="alert" className="type-meta text-clarify">
          {error}
        </p>
      ) : null}
      {instruction?.canConfirm || instruction?.canUndo ? (
        <div className="flex justify-end gap-2">
          {instruction.canConfirm ? (
            <Button disabled={action.isPending} onClick={() => action.mutate('confirm')}>
              {ASK_TILL_TEXTS.confirm}
            </Button>
          ) : null}
          {instruction.canUndo ? (
            <Button variant="secondary" disabled={action.isPending} onClick={() => action.mutate('undo')}>
              {ASK_TILL_TEXTS.undo}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
